import { useQuery } from "@tanstack/react-query";
import { prequalSiteAdminApi } from "@/api/prequalSiteAdmin";
import type { PrequalSiteCompletionListQuery } from "@/api/prequalSiteAdmin";
import { prequalSiteAdminKeys } from "./prequalSiteAdminKeys";

/** The broker's prequalification site + that site's recent completions.
 *  Gated server-side by Brokers.Read — the same permission that opens the
 *  broker record, so anyone on the page can read this. */
export function useBrokerPrequalSite(brokerId: string) {
  return useQuery({
    queryKey: prequalSiteAdminKeys.broker(brokerId),
    queryFn: () => prequalSiteAdminApi.forBroker(brokerId),
    enabled: !!brokerId,
  });
}

/** The firm's own site + completions + any member brokers running their own.
 *  Gated server-side by Brokerages.Read. */
export function useBrokeragePrequalSite(brokerageId: string) {
  return useQuery({
    queryKey: prequalSiteAdminKeys.brokerage(brokerageId),
    queryFn: () => prequalSiteAdminApi.forBrokerage(brokerageId),
    enabled: !!brokerageId,
  });
}

/** The buyer team's own site + that site's recent completions (D8). Gated
 *  server-side by Buyers.Read — the same permission that opens the team record.
 *
 *  `Own` or `None` only: a team neither inherits a site nor has a covering
 *  organization to be ambiguous about. */
export function useBuyerTeamPrequalSite(buyerTeamId: string) {
  return useQuery({
    queryKey: prequalSiteAdminKeys.buyerTeam(buyerTeamId),
    queryFn: () => prequalSiteAdminApi.forBuyerTeam(buyerTeamId),
    enabled: !!buyerTeamId,
  });
}

// ── Paged completions ───────────────────────────────────────────────────────
//
// The site is resolved SERVER-SIDE from the record id — these hooks pass paging,
// search and sort, never a brand. A record whose site does not resolve returns
// an empty page, so there is no "no site" branch to handle here beyond rendering
// zero rows.
//
// Both take `enabled` so a caller can hold the fetch off: while page 1 is being
// SEEDED from the site card (which already carries that exact window), and while
// the user lacks the permission that would make the request a 403.

/** One page of the broker's resolved site's completions. Gated server-side by
 *  Brokers.Read — the same permission that opens the broker record.
 *
 *  On an INHERITED site these are the FIRM's completions, shared with every
 *  broker who inherits it; there is no per-broker attribution, so the caller
 *  must label them brokerage-wide. */
export function useBrokerPrequalSiteCompletions(
  brokerId: string,
  query: PrequalSiteCompletionListQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: prequalSiteAdminKeys.brokerCompletions(brokerId, query),
    queryFn: () => prequalSiteAdminApi.brokerCompletions(brokerId, query),
    enabled: enabled && !!brokerId,
  });
}

/** One page of the firm's OWN site's completions. Gated server-side by
 *  Brokerages.Read. A member broker's separate site is never rolled in. */
export function useBrokeragePrequalSiteCompletions(
  brokerageId: string,
  query: PrequalSiteCompletionListQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: prequalSiteAdminKeys.brokerageCompletions(brokerageId, query),
    queryFn: () => prequalSiteAdminApi.brokerageCompletions(brokerageId, query),
    enabled: enabled && !!brokerageId,
  });
}

/** One page of the buyer team's OWN site's completions. Gated server-side by
 *  Buyers.Read. A team with no site returns an empty page. */
export function useBuyerTeamPrequalSiteCompletions(
  buyerTeamId: string,
  query: PrequalSiteCompletionListQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: prequalSiteAdminKeys.buyerTeamCompletions(buyerTeamId, query),
    queryFn: () => prequalSiteAdminApi.buyerTeamCompletions(buyerTeamId, query),
    enabled: enabled && !!buyerTeamId,
  });
}
